import UnleashService from '@/services/unleash'
import sentry from '@/services/sentry'
import store from '@/vuex/store'
import UnleashFlagTurnOnTime from '@/constant/UnleashFlagTurnOnTime'
import officeConstants from '@/constant/office'
import PresentationApi from '@/api/presentation'
import { calculateBillingCycleNumber } from '@/utils/billing.utils'
import { coalesce } from '@/utils/promise'
import { ROUTE_NAMES, PUBLIC_ROUTES } from '@/constant/routes'
import {
  MONTHLY_TO_YEARLY_DISCOUNT_EXPERIMENT_FLAG,
  DISCOUNT_VARIANT_NAME_PREFIX,
} from '@/constant/monthlyToYearlyUpgrade'
import { flags } from '@/utils/abtest.utils'
import {
  NEW_SLIDE_MARKETPLACE_FLAG,
  NEW_SLIDE_MARKETPLACE_VARIANTS,
} from '@/constant/newSlideMarketplaceExperiment.constant'
import {
  MY_FILES_MENU_FLAG,
  MY_FILES_MENU_VARIANTS,
} from '@/constant/myFilesMenuExperiment.constant'

import UserApi from '@/api/user'

const { unleashClient } = UnleashService

const { canUseNewUnleashMechanism, canUseCacheProfileOnLoad } = flags

// The Unleash SDK emits an 'error' event for every failed toggle fetch, which
// includes benign, self-healing network failures. On /pages/login the app
// rapidly switches context (anonymous -> authenticated), aborting in-flight
// requests, and many users are on flaky/blocked networks. Those surface as
// `TypeError: Failed to fetch` / `AbortError` and are non-actionable — the SDK
// retries and emits 'recovered'. We must not report them to Sentry (see
// STPANCRAS-PRESENTER-APP-13N4). Genuine `{ type: 'HttpError', code }` errors
// are not TypeErrors and carry no "failed to fetch" message, so they pass through.
const isBenignUnleashError = (error) => {
  if (!error) return true
  // In-flight request aborted by a context change / navigation
  if (error.name === 'AbortError' || error === 'AbortError') return true
  // Network-layer fetch failure: offline, DNS, CORS, connection reset, blocked.
  // Cross-browser messages: Chrome "Failed to fetch", Firefox "NetworkError
  // when attempting to fetch resource.", Safari "Load failed", Node "fetch failed".
  let msg = ''
  if (typeof error === 'string') {
    msg = error
  } else if (typeof error.message === 'string') {
    msg = error.message
  }
  return /failed to fetch|fetch failed|network ?error|load failed/i.test(msg)
}

// BOO-58 / AHA-43262: free-user monthly-vs-yearly direct-to-Stripe billing-cycle A/B test.
const DIRECT_FEATURE_UPGRADE_FLAG = 'experiment-direct-feature-upgrade'

// AHA-46375: PowerPoint add-in full-bleed resize. Read twice below (enabled + variant).
const PPT_AUTO_RESIZE_FLAG = 'AHA-46375-ppt-auto-resize'

// The variant lists platforms in human terms ("web"), not as Office.js PlatformType values
// ('OfficeOnline'), so normalise before comparing. An unknown token matches no platform.
const PPT_VARIANT_TOKEN_TO_PLATFORM = {
  pc: officeConstants.PC,
  mac: officeConstants.MAC,
  web: officeConstants.OFFICE_ONLINE,
  officeonline: officeConstants.OFFICE_ONLINE,
}

// The variant payload is a JSON array of platform tokens. Returns null when nothing usable
// is configured, which callers read as "all platforms".
const parsePptAutoResizePlatforms = (variant) => {
  const rawPlatforms = variant?.payload?.value
  if (!rawPlatforms) return null
  try {
    const platforms = JSON.parse(rawPlatforms)
    if (!Array.isArray(platforms) || !platforms.length) return null
    return platforms
  } catch (error) {
    return null
  }
}

// Slide types whose generate-options support is fully rolled out (flags retired).
// `categorise` is intentionally excluded — it is still gated behind
// `release-generate-answers-categorise` (not fully rolled out yet).
const GENERATE_OPTIONS_ROLLED_OUT_SLIDE_TYPES = [
  'pickAnswer',
  'pickImage',
  'typeAnswer',
  'matchPairs',
  'correctOrder',
  'poll',
]

const _state = {
  features: {}, // Store feature toggles
  isInitialized: false, // Track whether the client has initialized
  loading: {
    initialization: false,
    contextUpdate: false,
  },
  contextUpdateCounter: 0, // Counter for contextUpdate operations
  context: {},
  // anonymous is without any auth
  // routes: Sharing View, Login View, Register View
  hasLoadedAnonymousFlag: false,
  // other route that requires auth
  hasLoadedAuthFlag: false,
  // featureFlagInitCoalesced is used to prevent multiple feature flag init
  featureFlagInitCoalesced: null,
}

let isStartedUnleash = false

// AHA-42358: how long any feature may wait for user-scoped flags to load before
// giving up. On timeout the caller proceeds with flags-not-ready (the safe
// default for flag-gated code, e.g. plan benefit falls back to the legacy
// endpoint) rather than hanging forever when Unleash is slow/unavailable.
const DEFAULT_FLAG_READY_TIMEOUT_MS = 2000

// Reusable "user flags are loaded" gate. Resolves once setAuthFlag(true) fires,
// i.e. after updateContext has fetched the authenticated user's flags. Features
// that must read a flag immediately on app load (which races the Unleash fetch)
// can `await featureToggles/whenAuthFlagsReady` instead of evaluating too early.
let _authFlagsReadyResolve = null
let _authFlagsReadyPromise = new Promise((resolve) => { _authFlagsReadyResolve = resolve })
const _resolveAuthFlagsReady = () => {
  if (_authFlagsReadyResolve) {
    _authFlagsReadyResolve(true)
    _authFlagsReadyResolve = null
  }
}
// Re-arm the gate for the next context load (e.g. when the user flag is reset on
// leaving a shared presentation). No-op while a wait is still pending.
const _resetAuthFlagsReady = () => {
  if (!_authFlagsReadyResolve) {
    _authFlagsReadyPromise = new Promise((resolve) => { _authFlagsReadyResolve = resolve })
  }
}

const _updateAuthFlags = (commit, context) => {
  // We have 2 flag,
  // 1. anonymous
  // 2. auth
  if (!context?.isAuth) {
    commit('setAnonymousFlag', true)
  } else {
    commit('setAuthFlag', true)
  }
}

const mutations = {
  setFeatures(state) {
    const features = unleashClient.getAllToggles()
    const featureMap = features.reduce((map, feature) => {
      map[feature.name] = feature
      return map
    }, {})
    state.features = { ...featureMap }
  },
  setInitialized(state, initialized) {
    state.isInitialized = initialized
  },
  setLoading(state, { type, loading }) {
    if (type === 'contextUpdate') {
      if (loading) {
        state.contextUpdateCounter += 1
      } else {
        state.contextUpdateCounter = Math.max(0, state.contextUpdateCounter - 1)
      }

      state.loading.contextUpdate = state.contextUpdateCounter > 0
    } else {
      state.loading[type] = loading
    }
  },
  setContext(state, context) {
    state.context = {
      ...state.context,
      ...context,
    }
  },
  startUnleash() {
    if (canUseNewUnleashMechanism) {
      UnleashService.start()
      return
    }
    // Should be removed after the new mechanism is confirmed as stable
    if (isStartedUnleash) return
    isStartedUnleash = true
    unleashClient.start()
  },
  setAnonymousFlag(state, flag) {
    state.hasLoadedAnonymousFlag = flag
  },
  setAuthFlag(state, flag) {
    state.hasLoadedAuthFlag = flag
    // Drive the reusable readiness gate off the same signal callers already use.
    if (flag) {
      _resolveAuthFlagsReady()
    } else {
      _resetAuthFlagsReady()
    }
  },
  setFeatureFlagInitCoalesced(state, payload) {
    state.featureFlagInitCoalesced = payload
  },
}

const actions = {
  async initializeFeatureToggles({ commit, dispatch }) {
    commit('setLoading', { type: 'initialization', loading: true })

    try {
      unleashClient.on('initialized', async () => {
        const features = unleashClient.getAllToggles()
        if (!features || !features.length) {
          await unleashClient.updateContext({
            presenterLanguage: store.state.presenterLocale || 'en',
          })
          commit('startUnleash')
        }
        commit('setInitialized', true)
        commit('setLoading', { type: 'initialization', loading: false })
      })
      unleashClient.on('ready', async () => {
        commit('setFeatures')
        commit('setLoading', { type: 'initialization', loading: false })
        if (canUseCacheProfileOnLoad) {
          const context = unleashClient.getContext()
          _updateAuthFlags(commit, { ...context, isAuth: !!context.email })
        }
        if (unleashClient.getVariant(NEW_SLIDE_MARKETPLACE_FLAG)?.name === NEW_SLIDE_MARKETPLACE_VARIANTS.TREATMENT) {
          dispatch('slideMarketplace/fetchMarketplaceSlideTypes', null, { root: true }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to pre-fetch marketplace slide types:', err)
          })
        }
      })

      // on update context
      // More detail: https://docs.getunleash.io/reference/sdks/javascript-browser#available-events
      unleashClient.on('update', () => {
        commit('setFeatures')
        if (unleashClient.getVariant(NEW_SLIDE_MARKETPLACE_FLAG)?.name === NEW_SLIDE_MARKETPLACE_VARIANTS.TREATMENT) {
          dispatch('slideMarketplace/fetchMarketplaceSlideTypes', null, { root: true }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to pre-fetch marketplace slide types:', err)
          })
        }
        // log context
        // console.log('context :', JSON.stringify(state.context))
        // We have 2 flag,
        // 1. anonymous
        // 2. auth
        // CAUTION: probably race condition (context changes during inflight process)
        // Why removed? Because I realize this piece of code won't be called if the unleash detect nothing change from api
        // For short
        // 200 -> those code below run
        // 304 -> the on.('update') hook won't be triggered and those code below won't be run
        // if (!state?.context?.isAuth) {
        //   commit('setAnonymousFlag', true)
        // } else {
        //   commit('setAuthFlag', true)
        // }
      })
      unleashClient.on('error', (error) => {
        // Skip benign, self-healing network failures to avoid flooding Sentry
        // with non-actionable `Failed to fetch` noise (STPANCRAS-PRESENTER-APP-13N4).
        if (isBenignUnleashError(error)) return
        sentry.captureException(error, {
          tags: {
            source: 'featureToggles.module.js',
          },
        })
      })
      unleashClient.on('recovered', () => {
        // this event is only fired if after an error
        // the unleashClient successfully fetch the feature flags from the server
        // so we need to update the feature flags
        // refer to https://github.com/Unleash/unleash-js-sdk/blob/1746b0aa66c31603a3ecc52f0c7f13adb40a03dc/src/index.ts#L93
        commit('setFeatures')
      })
    } catch (error) {
      commit('setLoading', false)
      sentry.captureException(error)
    } finally {
      commit('setLoading', { type: 'initialization', loading: false })
    }
  },
  async updateFeatureContext({ state, commit }, context) {
    commit('setLoading', { type: 'contextUpdate', loading: true })
    commit('setContext', context)
    const contextToUse = { ...state.context }

    try {
      await unleashClient.updateContext(contextToUse)
    } catch (error) {
      // 'Failed to fetch' TypeErrors are expected when the Unleash client internally
      // aborts an in-flight request because a new updateContext() call was made before
      // the previous one completed (e.g., rapid context change from anonymous to
      // authenticated on the login page). These are not actionable and should not be
      // reported to Sentry
      // sentry.captureException(error)
    } finally {
      _updateAuthFlags(commit, contextToUse)
      commit('setLoading', { type: 'contextUpdate', loading: false })
      if (contextToUse.isAuth) {
        store.dispatch('featureSettings/fetchFeatureSettings')
      }
    }

    if (!canUseNewUnleashMechanism) {
      // redundant, already handle in the unleashClient.on('update', () => {
      commit('setFeatures')
    }
  },

  /**
    * Initialize user feature context with all necessary fields for experiments
    * @param {Object} context - Vuex action context
    * @param {Object} params - Parameters containing currentUser and presenterLanguage
    * @returns {Promise} - Promise resolving to context update result
    */
  async initializeUserFeatureContext1({
    dispatch, rootState,
  }, { currentUser, presenterLanguage }) {
    try {
      const { plan } = rootState

      const context = {
        // Base context fields (always included)
        userId: currentUser.id,
        email: currentUser.email,
        plan: currentUser.plan,
        accountId: currentUser.accountId || null,
        presenterLanguage,
        // Required so _updateAuthFlags fires setAuthFlag(true), which flushes
        // the mixpanel_auth queue introduced in AHA-40717. Without this, V1
        // users were stuck with hasLoadedAuthFlag=false and lost all auth-route
        // Mixpanel events (e.g. report-ai-insights CSAT clicks).
        isAuth: true,
        // Reset presentationCreatorEmail set by share-link flows, otherwise
        // it leaks into flag evaluation on the user's own pages.
        presentationCreatorEmail: null,

        // Context fields for experiment-AHA-30200-monthly-to-yearly
        billingCycle: plan?.billingCycle || null,
        billingCycleNumber: currentUser.myPlan?.subscription?.billing_cycle_anchor
          ? calculateBillingCycleNumber(currentUser.myPlan.subscription.billing_cycle_anchor)
          : null,
        qualifiedEvent: currentUser.firstEventStartedAt ? 'true' : 'false',
      }

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('initializeUserFeatureContext', context)
      }

      return dispatch('updateFeatureContext', context)
    } catch (error) {
      // Fallback to basic context if extended context fails
      sentry.captureException(error, {
        extra: {
          action: 'initializeUserFeatureContext',
          userId: currentUser?.id,
        },
      })

      const fallbackContext = {
        userId: currentUser.id,
        email: currentUser.email,
        plan: currentUser.plan,
        accountId: currentUser.accountId || null,
        presenterLanguage,
        isAuth: true,
        presentationCreatorEmail: null,
      }

      return dispatch('updateFeatureContext', fallbackContext)
    }
  },

  // Use this one to await the user flag update
  async initializeUserFeatureContext({
    dispatch,
  }, { currentUser, presenterLanguage }) {
    if (canUseNewUnleashMechanism) {
      return dispatch('initializeUserFeatureContext2', { currentUser, presenterLanguage })
    }
    return dispatch('initializeUserFeatureContext1', { currentUser, presenterLanguage })
  },
  // My 50 cents: awaiting context update is unstable IMO,
  // I am not sure the flag is ready and update to the state at that time
  // The ideal phase to confirm it to be ready is inside unleashClient.on('update', () => {
  // or the the setFeatures mutation to be more exact
  // We can set 1 more Unleash context value like:
  //  isAuth: true, and check it at the set time
  // But it's already worked and use by our colleagues, so let see if it works
  async initializeUserFeatureContext2({
    dispatch, rootState, commit, state,
  }, { currentUser, presenterLanguage }) {
    async function _doInitializeUserFeatureContext() {
      try {
        const { plan } = rootState

        if (!currentUser) {
          currentUser = rootState.currentUser
        }
        if (!currentUser) {
          currentUser = await UserApi.getUser()
        }

        const context = {
          // Base context fields (always included)
          userId: currentUser.id,
          email: currentUser.email,
          plan: currentUser.plan,
          accountId: currentUser.accountId || null,
          presenterLanguage,
          isAuth: true,
          // reset the presentationCreatorEmail context
          presentationCreatorEmail: null,

          // Context fields for experiment-AHA-30200-monthly-to-yearly
          billingCycle: plan?.billingCycle || null,
          billingCycleNumber: currentUser.myPlan?.subscription?.billing_cycle_anchor
            ? calculateBillingCycleNumber(currentUser.myPlan.subscription.billing_cycle_anchor)
            : null,
          qualifiedEvent: currentUser.firstEventStartedAt ? 'true' : 'false',
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('initializeUserFeatureContext', context)
        }

        return dispatch('updateFeatureContext', context)
      } catch (error) {
        // Fallback to basic context if extended context fails
        sentry.captureException(error, {
          extra: {
            action: 'initializeUserFeatureContext',
            userId: currentUser?.id,
          },
        })

        const fallbackContext = {
          userId: currentUser.id,
          email: currentUser.email,
          plan: currentUser.plan,
          accountId: currentUser.accountId || null,
          presenterLanguage,
          isAuth: true,
          // reset the presentationCreatorEmail context
          presentationCreatorEmail: null,
        }

        return dispatch('updateFeatureContext', fallbackContext)
      }
    }

    // This is to avoid multiple feature flag init
    // And we can invalidate it later if we need to
    if (!state.featureFlagInitCoalesced) {
      commit('setAuthFlag', false)
      commit('setFeatureFlagInitCoalesced', coalesce(() => _doInitializeUserFeatureContext()))
    }
    return state.featureFlagInitCoalesced.run()
  },
  async startUnleash({ commit }) {
    commit('startUnleash')
  },

  /**
   * AHA-42358: reusable gate that resolves once the authenticated user's flags
   * have loaded. Use this before any flag-gated logic that runs on app load —
   * reading a flag synchronously during boot races the Unleash fetch and yields
   * the default (off), which silently picks the wrong branch (e.g. the entitlement
   * endpoint). The user-flag load itself is kicked off by getCurrentUserAction;
   * this only waits for it.
   *
   * @returns {Promise<boolean>} true if flags loaded in time, false on timeout
   *   (caller should proceed with the safe flags-off default).
   */
  async whenAuthFlagsReady({ state }, payload) {
    const { timeoutMs = DEFAULT_FLAG_READY_TIMEOUT_MS } = payload || {}
    if (state.hasLoadedAuthFlag) return true
    let timer
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(false), timeoutMs) })
    try {
      return await Promise.race([_authFlagsReadyPromise, timeout])
    } finally {
      clearTimeout(timer)
    }
  },

  // Because sharing view is not auth required but we still need the flag
  async handlePresentationLoadFlagUnleashByShareCode({ dispatch }, shareCode) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfoByShareCode(shareCode)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
      return respFlagInfo.body
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      return error
      // do nothing
    }
  },
  async handlePresentationLoadFlagUnleash({ dispatch }, presentationId) {
    try {
      const respFlagInfo = await PresentationApi.getPresentationFlagInfo(presentationId)
      store.state.presentation.createdAt = respFlagInfo.body.createdAt
      if (UnleashService) {
        UnleashService.start()
        await UnleashService.ready
      }
      await dispatch('updateFeatureContext', {
        presentationCreatorEmail: respFlagInfo.body.creator.email,
      })
    } catch (error) {
      sentry.captureException(new Error('Update Presentation Unleash Error'), {
        extra: {
          error,
        },
      })
      // do nothing
    }
  },
}

export const comparePresentationCreatedDateWithFlag = (presentation, flag) => {
  if (!presentation || !flag || !UnleashFlagTurnOnTime[flag]) return false
  return new Date(presentation.createdAt) > UnleashFlagTurnOnTime[flag]
}

const _getters = {
  isFeatureEnabled: (state) => (featureName) => {
    return state.features[featureName] && state.features[featureName].enabled
  },
  featureFlags: (state) => () => {
    return state.features
  },
  hasLoadedAnonymousFlag: (state) => state.hasLoadedAnonymousFlag,
  hasLoadedAuthFlag: (state) => state.hasLoadedAuthFlag,
  getVariant: (state) => (featureName) => {
    return state.features[featureName] ? state.features[featureName].variant : {
      name: 'disabled',
      enabled: false,
      payload: null,
    }
  },
  isUnleashInitialized: (state) => {
    return state.isInitialized
  },
  isLoadingFeatures: (state) => {
    return state.loading.initialization || state.loading.contextUpdate
  },
  canUseReditusReferral: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40676-reditus-referral')
  },
  // Keep-alive preload for aha-slide-plugin canvases: while presenting, boot the
  // next plugin slide's iframe ahead of time (active:false) and reveal it on
  // navigation without recreating the iframe. Off => current close+recreate path.
  canUsePreloadAhaSlidePlugin: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46422-preload-aha-slide-plugin')
  },
  // AHA-46519: warm the editor bundle + TinyMCE during onboarding so the editor
  // mounts from cache. Off => no warm-up (current behaviour).
  canUseEditorWarmup: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-46519-editor-warmup')
  },
  canUseXmasDecoration: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-xmas-decoration')
  },
  canUseLeaderboardAudienceRefreshFix: (_, getters) => {
    return getters.isFeatureEnabled('PROD-2-leaderboard-audience-refresh')
  },
  canUseNewEnterpriseInquiriesForm: (_, getters) => {
    return getters.isFeatureEnabled('release-enterprise-inquiries-form')
  },
  // AHA-43391: hide the Team management item in the left sidebar for non
  // owner/admin members (enabled for ATB first).
  canUseHideTeamManagementForNonAdmin: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-43391-hide-team-management-non-admin')
  },
  // AHA-46323: a teamless user must be able to create a team from the sidebar
  // modal with a team name alone. Flag ON turns that modal into a pure
  // create-team form (no invitees) posting to POST /api/accounts; flag OFF keeps
  // today's "team name + at least one invitee -> POST /api/invitations" flow.
  canUseSidebarCreateTeam: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46323-create-team')
  },
  canUseNetworkIndicatorV2: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40903-update-network-indicator')
  },
  canUseCreateFromTemplateDeepLink: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-42424-create-from-template-deeplink')
  },
  canUseOnboardingIkeaChecklist: (_, getters) => {
    const variant = getters.getVariant('AHA-42696-onboarding-ikea-checklist-presenter')
    return variant?.name === 'onboarding-ikea-checklist-presenter'
  },
  // BOO-58 / AHA-43262: resolves the active arm of the direct-feature-upgrade
  // experiment. The Unleash strategy is constrained to free users (plan=free),
  // so this only returns a treatment arm for free users; everyone else (and a
  // flag that is off) falls through to `disabled`. Returns the raw variant name
  // ('monthly-upgrade' | 'yearly-upgrade' | 'disabled') so callers can both
  // branch on it AND forward it as the experiment dimension to analytics.
  directFeatureUpgradeVariant: (_, getters) => {
    return getters.getVariant(DIRECT_FEATURE_UPGRADE_FLAG)?.name || 'disabled'
  },
  canUseCorrectCropForImageReplace: (_, getters) => {
    return getters.isFeatureEnabled('release-correct-crop-for-image-replace')
  },
  // AHA-41738: gate the Design panel Themes/Customise sub-tab URL sync to use
  // router.replace (no history entry) instead of router.push. Off = today's
  // production behaviour unchanged (router.push).
  canUseEditorDesignTabHistoryReplace: (_, getters) => {
    return getters.isFeatureEnabled('release-editor-design-tab-history-replace')
  },
  canUseNewAutocomplete: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28900-autocomplete')
  },
  canUseGoogleDriveV2: (_, getters) => {
    return getters.isFeatureEnabled('release-google-drive-v2')
  },
  // Direct feature-upgrade modal: replaces the indigo upgrade popover + immediate
  // Stripe redirect with an in-app modal that shows the feature explanation and
  // lets the user pick a billing cycle (monthly/yearly) before checking out.
  // Variant-gated (not a plain on/off) so it can run as one arm of the upgrade
  // experiment; the new modal shows only for the `feature-upgrade-new-ui` variant.
  canUseFeatureUpgradeModal: (_, getters) => {
    return getters.getVariant('release-feature-upgrade-modal')?.name === 'feature-upgrade-new-ui'
  },
  canUseRequestAccessPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-28682-request-access-presentation')
  },
  canUseGenerateOptions: (_, getters, __, rootGetters) => {
    const { currentSlideType } = rootGetters
    if (currentSlideType === 'categorise') {
      return getters.isFeatureEnabled('release-generate-answers-categorise')
    }
    return GENERATE_OPTIONS_ROLLED_OUT_SLIDE_TYPES.includes(currentSlideType)
  },

  canUseQuestionSuggestions: (_, getters) => {
    return getters.isFeatureEnabled('release-question-suggestions')
  },
  isPresentationCreatedAfterTurnOnFlag: (_, __, rootState) => {
    return (flag) => {
      if (!rootState.presentation) return false
      return comparePresentationCreatedDateWithFlag(rootState.presentation, flag)
    }
  },

  canUseGoogleSlideAddonSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29230-google-slide-add-on-sync')
  },
  canUseGoogleSlidesSinglePrez: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39459-google-slides-single-prez')
  },
  canUsePptFileSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43099-ppt-file-sync')
  },
  canUseGoogleDriveSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43099-google-drive-sync')
  },
  canUseOneDriveSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43099-onedrive-sync')
  },
  canUseNewComunityTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-organising-community-templates')
  },
  canDisplayPricingDuringOnboarding: (_, getters) => {
    const variant = getters.getVariant('release-AHA-41565-onboarding-pricing-step')
    return variant?.name === 'treatment'
  },
  canUseShareableTemplate: (_, getters) => {
    return getters.isFeatureEnabled('release-shareable-template')
  },
  canUseReleaseNewDiscoverTemplates: (_, getters) => {
    return getters.isFeatureEnabled('release-new-discover-templates')
  },
  canUseExperimentCSATIntoTemplate: (_, getters) => {
    return getters.isFeatureEnabled('experiment-csat-into-template')
  },
  canUseThirdPartyIntegrationsInShareModal: (_, getters) => {
    return getters.isFeatureEnabled('release-promotion-3party-33784')
  },
  canUseExportRedshift: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift')
  },
  canUseExportRedshiftV4: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift-v4')
  },
  canUseExportRedshiftV5: (_, getters) => {
    return getters.isFeatureEnabled('release-export-redshift-v5')
  },
  fullReleaseRedshiftV5: (_, getters) => {
    return getters.isFeatureEnabled('full-release-export-redshift-v5')
  },
  // AHA-46264: route plain deck PDF export through the Cloudflare Worker (proxied by our API).
  canUseCloudflarePdfExport: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46264-cloudflare-worker-pdf')
  },
  canRemovePowerPointButton: (_, getters) => {
    return getters.isFeatureEnabled('release-remove-powerpoint-30061')
  },
  canUseAddinPowerpointAutoStart: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43226-ppt-auto-start')
  },
  canUsePptDefaultPresentation: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46192-ppt-default-presentation')
  },
  // AHA-46329: gate the PPT add-in multi-slide selector. Off => clicking a presentation
  // in the add-in's list opens the editor directly, exactly as before.
  canUsePptMultipleSelect: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46329-ppt-multiple-select')
  },
  // AHA-46375: gate the "resize the add-in frame to fill the slide" pass. The flag's
  // variant payload is a JSON array of the platforms the resize may run on, e.g.
  // ["PC", "mac"], so a platform that misbehaves can be dropped without a deploy. No
  // usable list (no variant, empty list, unparseable JSON) means every platform — a
  // misconfigured variant must not silently switch the feature off everywhere.
  // `addinPPTPlatform` is committed from Office.context.platform during add-in load,
  // before the resize runs; outside the add-in it is empty, so a variant list matches
  // nothing and the resize (which needs a PowerPoint host anyway) stays off.
  canUsePptAutoResize: (_, getters, rootState) => {
    if (!getters.isFeatureEnabled(PPT_AUTO_RESIZE_FLAG)) return false
    const platforms = parsePptAutoResizePlatforms(getters.getVariant(PPT_AUTO_RESIZE_FLAG))
    if (!platforms) return true
    return platforms.some((token) => {
      return PPT_VARIANT_TOKEN_TO_PLATFORM[String(token).trim().toLowerCase()] === rootState.addinPPTPlatform
    })
  },
  canUseGoogleSlidesPromote: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33785')
  },
  canUseIntegrationsSection: (_, getters) => {
    return getters.isFeatureEnabled('release-promote-googleslides-33786')
  },
  canUsePptVisualCue: (_, getters) => {
    return getters.isFeatureEnabled('DSN-814-ppt-visual-cue')
  },
  canUseMovePresentations: (_, getters) => {
    return getters.isFeatureEnabled('move-presentation')
  },
  canUseEveryoneHasAnswered: (_, __, ___, rootGetters) => {
    return rootGetters['liveStatsV2/canUseLiveStatsForQuizV2']
  },
  canUseDisableClientCount: (_, getters) => {
    return getters.isFeatureEnabled('AHA-45913-disable-client-count')
  },
  canUseTransactionalSlideCacheUpdate: (_, getters) => {
    return getters.isFeatureEnabled('transactional-slide-cache-update')
  },
  isEnabledNewIntegrations: (_, getters) => {
    return getters.isFeatureEnabled('is-enabled-new-integrations')
  },
  canUseNewPricing: (_, getters) => {
    return getters.isFeatureEnabled('release-new-pricing-presenter')
  },
  canUseSCIM: (_, getters) => {
    return getters.isFeatureEnabled('release-scim')
  },
  canUseScimManualManagement: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42627-scim-manual-management')
  },
  canResetPresentationTrackingContext: (_, getters) => {
    return getters.isFeatureEnabled('release-reset-presentation-tracking-context')
  },
  canUserOfferingMonthlyToYearly: (_, getters) => {
    // Allow the banner when the wording-v2 experiment has a valid arm...
    const wordingVariant = getters.getVariant('AHA-40735-abtest-wording-upgrade-v2')
    if (wordingVariant?.name === 'control' || wordingVariant?.name === 'exclusive-40-off') {
      return true
    }
    // ...OR when the BOO-11 discount-coupon experiment is active (any `discount-*` variant).
    // Without this check, enabling only AHA-40735-abtest-discount-coupon returns false here
    // and the banner is gated off before it can ever render.
    const discountVariant = getters.getVariant(MONTHLY_TO_YEARLY_DISCOUNT_EXPERIMENT_FLAG)
    return !!(discountVariant?.name?.startsWith(DISCOUNT_VARIANT_NAME_PREFIX))
  },
  // BOO-11: resolves the active monthly→yearly discount arm.
  // Returns { name, discountPercentage } for a discount arm, or null for control / flag-off.
  // discountPercentage is read from the Unleash variant payload when configured, with a
  // fallback to parsing it from the variant name (e.g. `discount-20pct` → 20). This means
  // it works whether or not the Unleash variant has a payload value set — percentages can
  // be managed entirely from the Unleash UI without a frontend deploy.
  monthlyToYearlyDiscountVariant: (_, getters) => {
    const variant = getters.getVariant(MONTHLY_TO_YEARLY_DISCOUNT_EXPERIMENT_FLAG)
    if (!variant?.name?.startsWith(DISCOUNT_VARIANT_NAME_PREFIX)) return null

    // Prefer payload value (configured on Unleash), fall back to parsing from variant name.
    const payloadValue = variant.payload?.value
    const fromPayload = payloadValue != null ? Number(payloadValue) : NaN
    const fromName = parseInt(variant.name.replace(/^discount-(\d+)pct$/, '$1'), 10)
    const discountPercentage = Number.isFinite(fromPayload) ? fromPayload : fromName

    if (!Number.isFinite(discountPercentage) || discountPercentage <= 0) return null
    return { name: variant.name, discountPercentage }
  },
  canUseNewResizeImageApi: (_, getters) => {
    return getters.isFeatureEnabled('AHA-37355-use-lambda-resize')
  },
  canUseReportSession: (_, getters) => {
    return getters.isFeatureEnabled('release-report-session')
  },
  canUseDirectBulkPurchaseLinks: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-38492-direct-bulk-purchase-links')
  },
  canUsePresentationSearchWithShared: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-38736-presentation-search-with-shared')
  },
  canHideAmount: (_, getters) => {
    return getters.isFeatureEnabled('release-my-plan-hide-amount')
  },
  canShowTeamFreeBadge: (_, getters) => {
    return getters.isFeatureEnabled('release-show-team-free-badge')
  },
  canUseButtonShareFolder: (_, getters) => {
    return getters.isFeatureEnabled('release-button-share-folder')
  },
  canUseISOStringForEventStartedAt: (_, getters) => {
    return getters.isFeatureEnabled('release-iso-string-for-event-started-at')
  },
  // https://ahaslides.atlassian.net/browse/AHA-39583
  // We change the current behavior, so I put this flag case we wanna revert
  // Current behavior: can't move a presentation to my presentation list
  // New behavior: can move a presentation to my presentation list
  canMovePresentationToMyPresentationList: (_, getters) => {
    return getters.isFeatureEnabled('release-allow-move-to-my-presentation')
  },
  canHideTeamSection: (_, getters) => {
    return getters.isFeatureEnabled('kill-switch-sidebar-team-section')
  },
  canUseSlidesReport: (_, getters) => {
    return getters.isFeatureEnabled('release-report-question')
  },
  canGetNewAccountMembers: (_, getters) => {
    return getters.isFeatureEnabled('release-new-api-get-account-member')
  },
  canUseAccountManager: (_, getters) => {
    return getters.isFeatureEnabled('release-account-manager')
  },
  canReplyToSender: (_, getters) => {
    return getters.isFeatureEnabled('release-reply-to-sender')
  },
  canUseParticipantProfiles: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40041-participant-profiles-full-list')
  },
  canUseReportAsIframe: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40370-report-as-iframe')
  },
  canUseParticipantProfileResponses: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40332-participant-profile-responses')
  },
  canUseNewSfxSettings: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-39704-presenting-sfx')
  },
  canUseMusicAndSoundSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-43591-music-and-sound-settings')
  },
  canUseIdeaBoardSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39912-idea-board-slide-type')
  },
  canUsePinOnImageSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40699-pin-on-image-slide-type')
  },
  canUseRankingSlideType: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40791-ranking-slide-type')
  },
  canUseDiagramSlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42445-diagram-slide')
  },
  canUseSurveySlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42639-survey-slide')
  },
  canUseSurveyInMoreTypes: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46167-survey-in-more-types')
  },
  canUseContentV2SlideType: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42560-content-v2-slide')
  },
  canShowNoResultPopup: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40816-show-no-response-popup')
  },
  canFixContextMenuPosition: (_, getters) => {
    return getters.isFeatureEnabled('AHA-29934-fix-context-menu')
  },
  canUseSessionReport: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40593-session-report')
  },
  canUsePresentationEditLink: (_, getters) => {
    return getters.isFeatureEnabled('AHA-32274-presentation-edit-link')
  },
  canUseAIConsentSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40635-release-ai-consent-settings')
  },
  canUseNewBackstage: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41193-new-ui-backstage')
  },
  canUseNewUiBackStage: (_, getters, __, rootGetters) => {
    return rootGetters.isBackstage && getters.isFeatureEnabled('AHA-41193-new-ui-backstage')
  },
  canUseAIConsentTeamTab: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-40635-ai-consent-team-tab')
  },
  canUseNewTeamManagement: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-39002-team-management')
  },
  canUseEmbedTeamApp: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41755-embed-team-app')
  },
  canUseAIConsentTeamSettings: (_, getters) => {
    return getters.isFeatureEnabled('AHA-40914-release-ai-consent-team-settings')
  },
  canUsePresentationDefaults: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41118-presentation-defaults')
  },
  canUseSamePricingDisplay: (_, getters) => {
    const variant = getters.getVariant('release-AHA-41159-same-pricing-display')
    return variant?.name === 'treatment'
  },
  canUseTeamSettingsPaywall: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-41396-team-settings-paywall')
  },
  canUseWebflowIntegrationPage: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41735-use-webflow-integration-page')
  },
  canUseCopyCastingUrl: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41709-copy-casting-url')
  },
  canUseInlineCsatForAllSlideTypes: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41701-inline-csat-for-all-slide-types')
  },
  canUseCreateWithAIHomepage: (_, getters) => {
    const variant = getters.getVariant('release-AHA-41524-create-with-ai-homepage')
    return variant?.name === 'treatment'
  },
  canShowCreateWithAIOnHomepage: (_, getters, rootState, rootGetters) => {
    const flagEnabled = getters.canUseCreateWithAIHomepage
    const aiDisabled = rootGetters['featureSettings/isFeatureDisabled']('aiContentGeneration')
    const isAuthenticated = !!rootState.currentUser?.id
    return flagEnabled && !aiDisabled && isAuthenticated
  },
  canUseInlineCSATForBackstage: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41701-inline-csat-for-backstage')
  },
  canUsePresentingControlAndStatusPanels: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41850-presenting-control-and-status-panels')
  },
  // Host-rendered slide-plugin action buttons. Requires the new control bar
  // (the buttons teleport into its ncb-center) plus its own rollout flag.
  canUsePluginActionButtons: (_, getters) => {
    return getters.canUsePresentingControlAndStatusPanels
      && getters.isFeatureEnabled('AHA-43142-plugin-buttons')
  },
  canUseFakeDoorUploadLimit: (_, getters, rootState) => {
    return getters.isFeatureEnabled('AHA-41824-release-fake-door-upload-limit')
      && rootState.currentUser?.plan === 'free'
  },
  canUseUpdateEditorUi: (_, getters) => {
    const variant = getters.getVariant('AHA-42401-update-editor-ui')
    return variant?.name === 'new-ui'
  },
  canUseUpdateSettingModal: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42401-update-setting-modal')
  },
  canUseTemplateLibraryAnywhere: (_, getters) => {
    return getters.isFeatureEnabled('release-AHA-42423-template-library-anywhere')
  },
  canUseAdminAnalyticsStreamlit: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42209-admin-analytics-streamlit')
  },
  canUseShowHideControlPresenting: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41564-show-hide-control-presenting')
  },
  canSeparateVolumeControl: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42126-separate-the-volume-control')
  },
  canUseSidebarRedesign: (_, getters, rootState) => {
    // Sidebar redesign must never apply to the Google Slides integration.
    if (rootState.integrationPlatform === 'GoogleSlide') return false
    return getters.isFeatureEnabled('release-AHA-42765-sidebar-redesign')
  },
  // Two-level "My Files" menu (All / Presentations / Surveys) that replaces the
  // Presentations branch. It only renders inside the redesigned sidebar, and the
  // caller must also check that the aha-workspace embed app is registered —
  // see embedApp/hasWorkspaceApp.
  //
  // Gated on the variant NAME, not on enabled/disabled: a boolean check cannot tell
  // "control" apart from "the flag never loaded", and conflating the two corrupts the
  // Mixpanel exposure split. Anything that isn't an explicit treatment is control.
  canUseMyFilesMenu: (_, getters) => {
    const variant = getters.getVariant(MY_FILES_MENU_FLAG)
    return variant?.name === MY_FILES_MENU_VARIANTS.TREATMENT
  },
  canUseNewSlideMarketPlace: (_, getters) => {
    const variant = getters.getVariant(NEW_SLIDE_MARKETPLACE_FLAG)
    return variant?.name === NEW_SLIDE_MARKETPLACE_VARIANTS.TREATMENT
  },
  canUseNewEntitlementApi: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42358-new-entitlement-api')
  },
  canUseUpgradeInPreview: (_, getters) => {
    return getters.isFeatureEnabled('AHA-42488-soft-limit')
  },
  canUseEditorNoteSync: (_, getters) => {
    return getters.isFeatureEnabled('AHA-41303-editor-note-sync')
  },
  // AHA-42054 follow-up — gates the presenter checkout/upgrade price alignment
  // (route through the variant-aware v4 endpoints using the lineup's Stripe
  // lookupKey). Release toggle (boolean) because it guards a change to the legacy
  // checkout path; off restores the canonical base-price behaviour exactly.
  canUsePriceAlignmentFix: (_, getters) => {
    return getters.isFeatureEnabled('release-price-alignment-fix')
  },
  // BOO-149 / AHA-46195: gate the long-term (2-year + Lifetime) upgrade banner's
  // visibility. Flag off restores the pre-BOO-149 behaviour (banner never shown).
  canUseMultiYearPrices: (_, getters) => {
    return getters.isFeatureEnabled('AHA-46195-multi-year-prices')
  },
  // AHA-42054 — whether the Essential plan is hidden from the free-user upgrade
  // funnel. Single source of truth is the shared pricing lineup
  // (pricingLineup/isEssentialExcluded): the presenter reuses the SAME variant the
  // pricing page resolved server-side (request-geo + shared session), so the two
  // surfaces can never disagree about a given viewer. The raw getter (no plan gate)
  // is for callers that decide free-ness themselves (e.g. the plan-benefit api,
  // which reads it off the response body).
  canRemoveEssentialPlan: (_, __, ___, rootGetters) => {
    return rootGetters['pricingLineup/isEssentialExcluded']
  },
  // Component-facing variant: the override only applies to free users, so fold
  // the free-plan check in here once instead of re-deriving it at every CTA.
  canRemoveEssentialPlanForFreeUser: (_, __, rootState, rootGetters) => {
    return rootGetters['pricingLineup/isEssentialExcluded']
      && rootState.plan?.group === 'free'
  },
  // AHA-39565: gate the email verification badge shown next to the email in
  // My profile > Personal info. Flag off restores the plain email text.
  canUseEmailVerificationComponent: (_, getters) => {
    return getters.isFeatureEnabled('AHA-39565-release-email-verification-component')
  },
  // Gates the in-app header upgrade modal: on the `header-popover` or
  // `header-modal-yearly-only` variant, clicking "Upgrade" in the main header opens
  // HeaderUpgradeModal (Pro plan + pricing) instead of routing to the pricing page /
  // user-plan. Any other variant (or flag off) restores the original header upgrade
  // behaviour.
  canUseHeaderUpgradeModal: (_, getters) => {
    const variant = getters.getVariant('AHA-45905-release-header-upgrade-modal')?.name
    return variant === 'header-popover' || variant === 'header-modal-yearly-only'
  },
  // AHA-45905: on the `header-modal-yearly-only` variant the header upgrade modal drops
  // the Monthly billing option and offers only the Yearly plan. Any other variant (or
  // flag off) keeps both billing cycles.
  isHeaderUpgradeModalYearlyOnly: (_, getters) => {
    return getters.getVariant('AHA-45905-release-header-upgrade-modal')?.name === 'header-modal-yearly-only'
  },
}

export const isFeatureEnabled = (rootGetters, featureName) => {
  return rootGetters['featureToggles/isFeatureEnabled'](featureName)
}

export const getVariant = (rootGetters, featureName) => {
  return rootGetters['featureToggles/getVariant'](featureName)
}

export const isPresentationCreatedAfterTurnOnFlag = (rootGetters, flag) => {
  return rootGetters['featureToggles/isPresentationCreatedAfterTurnOnFlag'](flag)
}

export const isPresentationResetAfterTurnOnFlag = (presentation, flag) => {
  if (!presentation ||
    !presentation.resetTimeStamp ||
    !flag ||
    !UnleashFlagTurnOnTime[flag]
  ) {
    return false
  }
  // resetTimeStamp is a string, so need to convert it to number before parsing to Date object
  return new Date(Number(presentation.resetTimeStamp)) > UnleashFlagTurnOnTime[flag]
}

function _handleResetFeatureFlagInitCoalescedPlugin(_store, action) {
  if (action.type !== 'routeChangeAction') return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const isExitingEditorScreen = fromRoute === ROUTE_NAMES.PRESENTATION_DETAIL
    && toRoute !== ROUTE_NAMES.PRESENTATION_DETAIL

  const isExitingSharingView = fromRoute === ROUTE_NAMES.PRESENTATION_SHARE
    && toRoute !== ROUTE_NAMES.PRESENTATION_SHARE

  // Applied routes
  const isExistingAppliedRoute = isExitingEditorScreen || isExitingSharingView

  // Check if the presentation is shared or owned
  const isOwnedPresentation = _store?.state?.presentation?.userId === _store?.state?.currentUser?.id

  if (isExistingAppliedRoute && !isOwnedPresentation) {
    // Reset the User Flag using current user context after existing the Shared Route
    _store.commit('featureToggles/setFeatureFlagInitCoalesced', null)
    _store.dispatch('featureToggles/initializeUserFeatureContext', {
      currentUser: _store?.state?.currentUser,
      presenterLanguage: _store?.state?.presenterLocale,
    })
  }
}

function _handleGetCurrentUserPlugin(_store, action) {
  if (action.type !== 'routeChangeAction') return

  const fromRoute = action.payload?.from?.name
  const toRoute = action.payload?.to?.name

  const fromPublicRoute = PUBLIC_ROUTES.includes(fromRoute)
  const toAuthGuardRoute = !PUBLIC_ROUTES.includes(toRoute)

  if (fromPublicRoute && toAuthGuardRoute) {
    _store.dispatch('getCurrentUserAction')
  }
}

const resetFeatureFlagInitCoalescedPlugin = (_store) => {
  if (!canUseNewUnleashMechanism) return
  _store.subscribeAction((action) => {
    // Handle the Reset Feature Flag after existing the Shared Route
    _handleResetFeatureFlagInitCoalescedPlugin(_store, action)
  })
}

const getCurrentUserPlugin = (_store) => {
  if (!canUseNewUnleashMechanism) return
  _store.subscribeAction((action) => {
    // Because getCurrentUserAction is not trigger inside login or signUp component but via child component
    // So there are nothing registered (mixpanel, sentry, ...)
    // We need to manually trigger the getCurrentUserAction
    _handleGetCurrentUserPlugin(_store, action)
  })
}

const plugins = [
  resetFeatureFlagInitCoalescedPlugin,
  getCurrentUserPlugin,
]

export default {
  namespaced: true,
  state: _state,
  mutations,
  actions,
  getters: _getters,
  plugins,
}
